import PropTypes from 'prop-types';
import isNumber from 'lodash/isNumber';
import sample from 'lodash/sample';
import {roundTo} from 'round-to';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import Stack from '@mui/material/Stack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Typography from '@mui/material/Typography';

import { GoalIcon } from '../../styles/icons';
import CustomBox from '../common/CustomBox';
import CustomSelect from '../common/CustomSelect';
import GoalEditor from './GoalEditor';
import { ArrowUpIcon } from '../../styles/icons';
import { MAX_WITH_MOBILE_MEDIA } from '../../config/getTheme';
import './MetricsOverview.scss';
import config from "../../config/config";

const calculateTrendPercentage = (current, prior) => {
  if (!isNumber(current) || !isNumber(prior)) return null;
  if (prior === 0) return null;
  const diff = current / prior;
  const ratioDiff = (
    diff > 1
      ? diff - 1
      : 1 - diff
  );
  return ratioDiff * 100;
};

const getGoalText = percentage => {
  if (percentage > 0.75) return 'You\'re almost at your goal! 🏁';
  if (percentage > 0.50) return 'You\'re over halfway to your goal! Keep up the good work! 💪';
  return 'You\'re less than halfway to your goal. Keep at it! 🏃‍♀️';
};

const tips = [
  'Reach out to past patients',
];

const Metric = props => {
  let color, TrendIcon;
  if (props.trendPercentage > 0) {
    color = 'success.main';
    // trendDirection = 'up';
    TrendIcon = ArrowUpIcon;
  }
  if (props.trendPercentage < 0) {
    color = 'error.main';
    // trendDirection = 'down';
    TrendIcon = ArrowDownwardIcon;
  }
  if (props.trendPercentage === 0) {
    color = 'light.main';
    // trendDirection = 'none';
    TrendIcon = LinearScaleIcon;
  }
  return (
    <Stack direction='row' className='Metric'>
      <Stack>
        <Typography
          variant='largeInter'
          color='text.gray'
          noWrap
          sx={{
            lineHeight: '1rem',
            [MAX_WITH_MOBILE_MEDIA]: {
              fontSize: '0.75rem',
            },
          }}
        >
          {props.label}
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center' style={{ position: 'relative' }}>
          <Typography
            variant='large600TightInter'
            color='text.primary'
            sx={{
              lineHeight: '1.25rem',
              [MAX_WITH_MOBILE_MEDIA]: {
                fontSize: '0.875rem',
              },
            }}
          >
            {props.value || 0}
          </Typography>
          {
            props.Button &&
            <Box
              sx={{
                '& button': {
                  padding: 0,
                },
              }}
            >
              {props.Button}
            </Box>

          }
        </Stack>
      </Stack>
      <Stack className='Metric__trend-percentage-stack'>
        {
          props.trendPercentage || props.trendPercentage === 0
            ?
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <TrendIcon color={color.replace('.main', '')} />
              <Typography
                variant='large600TightInter'
                color={color}
                sx={{
                  [MAX_WITH_MOBILE_MEDIA]: {
                    fontSize: '0.875rem',
                  },
                }}
              >
                {roundTo(props.trendPercentage, 2)}%
              </Typography>
            </Stack>
            : null
        }
      </Stack>
    </Stack>
  );
};

const MetricsOverview = props => {
  const {
    metricsSessionCount,
    metricsSessionGoal,
    metricsSessionPriorPeriodCount
  } = props;
  const percentOfGoal = (metricsSessionCount / metricsSessionGoal) || 0;
  const priorMonthPercentOfGoal = (metricsSessionPriorPeriodCount / metricsSessionGoal) || 0;

  return (
    <CustomBox className='MetricsOverviewComponent'>
      <Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} marginBottom={{ xs: 0, sm: '13px' }} className='MetricsOverview__head'>
          <Stack direction='row' alignItems='center' className='MetricsOverview__head__left'>
            <Stack direction='row' alignItems='center' justifyContent='center' className='MetricsOverview__trending-up-circle'>
              <TrendingUpIcon sx={{ color: 'white', width: '1.21875rem', height: '1.21875rem' }} />
            </Stack>
            <Typography
              variant='h2'
              color='text.primary'
              sx={{
                lineHeight: '1.875rem',
                marginLeft: '15px',
                [MAX_WITH_MOBILE_MEDIA]: {
                  fontSize: '1.125rem',
                },
              }}
            >
              Overview
            </Typography>
          </Stack>
          <Box className='MetricsOverview__head__right'>
            <CustomSelect
              className='CustomSelect-style'
              textAlign='right'
              noBorder='true'
              colorText={config().colors.primary}
              colorIcon={config().colors.primary}
              defaultValue={props.metricsPeriod}
              disableClearable
              onChange={e => props.onMetricsPeriodChange(e.target.value)}
              options={[
                { label: 'Month to Date', value: 'month' },
                { label: 'Week to Date', value: 'week' },
                { label: 'Biweekly to Date', value: 'biweek' },
                { label: 'Last 30 Days', value: '30day' },
                { label: 'Last 14 Days', value: '14day' },
                { label: 'Last 7 Days', value: '7day' },
              ]}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  [MAX_WITH_MOBILE_MEDIA]: {
                    padding: "0px !important",
                    textAlign: "left !important"
                  },
                },
                '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                  color: "#0BA9F5",
                  fontWeight: 400,
                  [MAX_WITH_MOBILE_MEDIA]: {
                    fontSize: "0.75rem !important",
                    padding: "0px !important",
                    textAlign: "left !important"
                  },
                },
              }}
            />
            <Divider className='MetricsOverview__divider' />
          </Box>
        </Stack>
        <Stack direction='row' className='MetricsOverview__goal-text'>
          <Stack spacing={1}>
            <Stack direction='row' alignItems='center' justifyContent='center' className='MetricsOverview__goal'>
              <CircularProgress
                size={27}
                sx={{
                  color: (theme) =>
                    theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
                }}
                variant='determinate'
                value={100}
              />
              <CircularProgress
                size={27}
                color={percentOfGoal === 0 ? 'light' : 'primary'}
                variant='determinate'
                value={percentOfGoal === 0 ? 100 : percentOfGoal * 100}
                sx={{
                  animationDuration: '550ms',
                  position: 'absolute',
                  left: 0,
                }}
              />
              <Stack direction='row' alignItems='center' justifyContent='center' className='MetricsOverview__goal__icon'>
                <GoalIcon />
              </Stack>
            </Stack>
            <Typography
              className='MetricsOverview__goal__percent'
              variant='largeBoldLooseInter'
              lineHeight='normal'
              sx={{
                marginTop: '4px !important',
                marginLeft: '2px !important',
                [MAX_WITH_MOBILE_MEDIA]: {
                  fontSize: '0.8125rem',
                  textAlign: 'center',
                },
              }}
            >
              {roundTo(percentOfGoal * 100, 2)}%
            </Typography>
          </Stack>
          <Stack className='MetricsOverview__note'>
            <Typography
              variant='largeInter'
              color='text.primary'
              noWrap
              sx={{
                lineHeight: 'normal',
                letterSpacing: '-0.15px',
                marginBottom: '5px',
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
                display: "-webkit-box",
                whiteSpace: "normal",
                [MAX_WITH_MOBILE_MEDIA]: {
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                },
              }}
            >
              {getGoalText(percentOfGoal)}
            </Typography>
            {/* <Typography
              variant='largeInter'
              color='text.gray'
              sx={{
                lineHeight: 'normal',
                letterSpacing: '-0.03125rem',
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
                display: "-webkit-box",
                whiteSpace: "normal",
                [MAX_WITH_MOBILE_MEDIA]: {
                  fontSize: '0.875rem',
                },
              }}
            >
              Tips: {sample(tips)}
            </Typography> */}
          </Stack>
        </Stack>
        <Divider className='MetricsOverview__divider' />
        <Stack
          direction='row'
          paddingTop='7px'
          paddingLeft='9px'
          paddingBottom='4px'
        >
          <Metric
            label='Sessions'
            value={metricsSessionCount}
            trendPercentage={calculateTrendPercentage(
              metricsSessionCount,
              metricsSessionPriorPeriodCount,
            )}
          />
          <Metric
            label='My Goal'
            value={metricsSessionGoal}
            trendPercentage={null}
            Button={
              <GoalEditor
                provider={props.provider}
                postSave={props.refetch}
              />
            }
          />
          <Metric
            label='Actuation'
            value={`${roundTo(percentOfGoal * 100, 2)}%`}
            trendPercentage={calculateTrendPercentage(
              percentOfGoal,
              priorMonthPercentOfGoal
            )}
          />
          {/*<Grid item md='auto'  sm={4}>
              <Metric
                label='Earnings'
                value={2000}
                trendPercentage={-5.5}
              />
            </Grid>*/}
        </Stack>
        <Divider className='MetricsOverview__divider' />
      </Stack>
    </CustomBox>
  );
};

MetricsOverview.propTypes = {
  metricSessionCount: PropTypes.number,
  metricsSessionPriorPeriodCount: PropTypes.number,
  metricsSessionGoal: PropTypes.number,
  refetch: PropTypes.func,
};

export default MetricsOverview;
